import { Legend } from "./Legend";
import { BOSH_USLUB, ulushFoiz, yumaloq } from "./scale";

export interface ColumnSegment {
	value: number;
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	label: string;
}

export interface Column {
	key: string;
	/** O'q ostidagi yorliq (tarjima qilingan). */
	label: string;
	/** Pastdan yuqoriga qatlamlar. */
	stack: ColumnSegment[];
	/** ⚠️ Formatlangan to'liq matn — ustunning `title` i va `aria-label` qismi. */
	tooltip: string;
	/** Ustun ustidagi qisqa yorliq (masalan jami son). */
	topLabel?: string;
}

export interface ColumnChartProps {
	columns: Column[];
	/** Chizma maydoni balandligi, px. */
	height?: number;
	/** Har ustun markazidan o'tuvchi chiziq — `values[i]` ↔ `columns[i]`. */
	average?: { values: number[]; label: string };
	/** Gorizontal uzuq chegara chizig'i. */
	threshold?: { value: number; label: string };
	/**
	 * Berilmasa — ustun yig'indilari, `threshold` va `average` ning eng
	 * kattasi (chiziqlar yuqorida kesilmasin).
	 */
	maxValue?: number;
	/** Bo'sh ro'yxat matni (reja imzosidan kengaytirilgan). */
	empty?: string;
	/**
	 * `false` — `average` legendasi chizilmaydi (u karta sarlavhasida
	 * bo'lsa ikki nusxa chiqmasin). Standart `true`.
	 */
	showLegend?: boolean;
}

/** Bitta ustunning eng katta kengligi, px. */
const USTUN_MAX = 56;
/** `gap-1` — ustunlar orasidagi bo'shliq, px. */
const USTUN_BOSHLIQ = 4;

function jami(ustun: Column): number {
	return ustun.stack.reduce((a, s) => a + Math.max(0, s.value), 0);
}

/**
 * Qatlamli ustunlar diagrammasi (sof CSS, chiziqlar uchun SVG qatlam).
 *
 * ⚠️ **Ustun (`data-column-stack`) balandligi — chizma maydoniga
 * nisbatan** (`yig'indi / shkala`); **bo'lak balandligi esa USTUN
 * ICHIDAGI ulush** (CSS foizi ota elementdan olinadi). Shkaladan oshgan
 * ustun 100% da kesiladi va bo'laklari mutanosib qisqaradi.
 *
 * ⚠️ `data-column*` / `data-threshold` / `data-average` — TEST
 * KONTRAKTI (hisobot sahifalari testlari shu atributlardan o'qiydi).
 *
 * ⚠️ SVG qatlami `preserveAspectRatio="none"` — ichida matn YO'Q
 * (cho'zilib ketardi); yorliqlar HTML.
 */
export function ColumnChart({
	columns,
	height = 160,
	average,
	threshold,
	maxValue,
	empty,
	showLegend = true,
}: ColumnChartProps) {
	if (columns.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const shkala =
		maxValue ??
		Math.max(
			0,
			...columns.map(jami),
			threshold?.value ?? 0,
			...(average?.values.filter(Number.isFinite) ?? [])
		);
	const n = columns.length;
	/*
	 * ⚠️ Ustun kengligi cheklangan: `daily` faqat ma'lumotli kunlarni
	 * oladi, ya'ni bitta kunlik oynada `flex-1` ustun butun kenglikka
	 * cho'zilib yaxlit blok bo'lardi. Cheklov ICHKI qutiga qo'yiladi —
	 * ustun foizlari, `average` va yorliqlar qatori bir xil qutiga
	 * tayanadi va o'zaro mos qoladi.
	 */
	const qutiKengligi = `${n * USTUN_MAX + (n - 1) * USTUN_BOSHLIQ}px`;
	const ortachaNuqtalar = average
		? average.values
				.slice(0, n)
				.map((v, i) => `${yumaloq(((i + 0.5) / n) * 100)},${yumaloq(100 - ulushFoiz(v, shkala))}`)
				.join(" ")
		: "";

	return (
		<div>
			<div
				role="img"
				aria-label={columns.map((u) => u.tooltip).join("; ")}
				style={{
					height: `${height}px`,
					// ⚠️ `topLabel` 100% li ustun ustiga shu bo'shliqqa chiqadi.
					paddingTop: columns.some((u) => u.topLabel !== undefined) ? 16 : 0,
					borderBottom: "1px solid var(--chart-grid)",
				}}
			>
				{/* ⚠️ Ichki maydon: ustun foizlari va `absolute` chiziqlar BIR XIL qutiga tayansin. */}
				<div
					className="relative mx-auto flex h-full items-stretch gap-1"
					style={{ maxWidth: qutiKengligi }}
				>
					{columns.map((ustun) => (
						<Ustun key={ustun.key} ustun={ustun} shkala={shkala} />
					))}

					{threshold ? (
						<div
							data-threshold
							aria-hidden="true"
							className="pointer-events-none absolute right-0 left-0"
							style={{
								bottom: `${ulushFoiz(threshold.value, shkala)}%`,
								borderTop: "1px dashed var(--chart-axis)",
							}}
						>
							<span
								className="absolute right-0 bottom-0.5 text-[10px]"
								style={{ color: "var(--chart-axis)" }}
							>
								{threshold.label}
							</span>
						</div>
					) : null}

					{average ? (
						<svg
							aria-hidden="true"
							viewBox="0 0 100 100"
							preserveAspectRatio="none"
							className="pointer-events-none absolute inset-0 h-full w-full"
							style={{ overflow: "visible" }}
						>
							<polyline
								data-average
								points={ortachaNuqtalar}
								fill="none"
								stroke="var(--chart-ink)"
								strokeWidth={1.5}
								strokeLinejoin="round"
								vectorEffect="non-scaling-stroke"
							/>
						</svg>
					) : null}
				</div>
			</div>

			<div className="mx-auto mt-1 flex gap-1" style={{ maxWidth: qutiKengligi }}>
				{columns.map((ustun) => (
					<div
						key={ustun.key}
						data-column-label
						className="min-w-0 flex-1 truncate text-center text-[10px]"
						style={{ color: "var(--chart-axis)" }}
					>
						{ustun.label}
					</div>
				))}
			</div>

			{average && showLegend ? (
				<div className="mt-2">
					<Legend items={[{ label: average.label, color: "var(--chart-ink)", shape: "line" }]} />
				</div>
			) : null}
		</div>
	);
}

function Ustun({ ustun, shkala }: { ustun: Column; shkala: number }) {
	const yigindi = jami(ustun);
	const balandlik = ulushFoiz(yigindi, shkala);
	const bolaklar = ustun.stack.filter((s) => s.value > 0);

	return (
		<div
			data-column
			title={ustun.tooltip}
			className="relative flex min-w-0 flex-1 flex-col justify-end"
		>
			{ustun.topLabel === undefined ? null : (
				<div
					data-column-top
					// ⚠️ `shrink-0`: `truncate` min-height ni 0 qiladi — 100% li
					// ustunda yorliq nolga siqilib yo'qolardi.
					className="shrink-0 truncate text-center text-[10px] tabular-nums"
					style={{ color: "var(--chart-ink)" }}
				>
					{ustun.topLabel}
				</div>
			)}
			{/*
			 * ⚠️ `shrink-0` MAJBURIY: `overflow-hidden` avtomatik min-height ni
			 * 0 qiladi va `topLabel` + qatlam ustundan oshganda flexbox qatlamni
			 * yorliq balandligicha qisqartirardi (~89% dan balandlari bir xil,
			 * 100% li ustun chiziqlardan pastda). `shrink-0` bilan ortiqcha qism
			 * `justify-end` orqali `paddingTop` bo'shlig'iga chiqadi (`Histogram`
			 * dagi naqsh). happy-dom layout hisoblamaydi — test faqat klassni
			 * qo'riqlaydi.
			 */}
			<div
				data-column-stack
				className="flex shrink-0 flex-col-reverse overflow-hidden rounded-t-sm"
				style={{ height: `${balandlik}%` }}
			>
				{/* ⚠️ Tartib qat'iy — indeksli kalit takroriy `label` da ham noyob. */}
				{bolaklar.map((s, indeks) => (
					<i
						key={`${s.label}-${indeks}`}
						data-column-segment
						className="block w-full shrink-0"
						style={{
							height: `${ulushFoiz(s.value, yigindi)}%`,
							backgroundColor: s.color,
						}}
					/>
				))}
			</div>
		</div>
	);
}
